<template>
  <div
    :class="[$style.card, { [$style.is24]: !isHg }]"
    data-test="stacked-cards-renovation-planner"
  >
    <div :class="$style.content">
      <HgBadge v-bind="isHg ? { theme: 'info' } : {}" :class="$style.badge">
        {{ t('myProperties.landing.renovationPlanner.badge') }}
      </HgBadge>

      <h2 :class="$style.title">{{ t('myProperties.landing.renovationPlanner.title') }}</h2>

      <p :class="$style.description">
        {{ t('myProperties.landing.renovationPlanner.description') }}
      </p>

      <HgButton
        size="large"
        :theme="isHg ? 'secondary' : 'primary'"
        :class="$style.cta"
        @click="goToOverview"
      >
        {{ t('myProperties.landing.renovationPlanner.cta') }}
      </HgButton>
    </div>

    <div
      :class="[$style.imageWrapper, { [$style.imageWrapperHg]: isHg }]"
      :style="isHg ? { backgroundImage: `url(${bgdStackHg})` } : undefined"
    >
      <div :class="$style.visualContainer">
        <img
          v-if="!isHg"
          :src="bgdStackIs24"
          alt=""
          :class="$style.backgroundGraphic"
          loading="lazy"
        />
        <img
          :src="phoneMockupImage"
          :alt="t('myProperties.landing.renovationPlanner.title')"
          :class="$style.image"
          loading="lazy"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import bgdStackHg from '@/assets/images/landing-renovation-planner/bgd-stack-hg.png?url';
import bgdStackIs24 from '@/assets/images/landing-renovation-planner/bgd-stack-is24.png?url';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { isPlatformHg } from '@/helpers/Platform/Platform';
import { getPropertyLandingImageUrl } from '@/helpers/Url/Url';
import { OVERVIEW_PAGE_PATH_SOURCE_LANDING } from '@/router/routes/constants';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { HgBadge, HgButton, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const isHg = isPlatformHg();
const { locale, t } = useI18n();
const router = useRouter();
const { trackClickEventWithTreatment } = useTrackingWithTreatment();

const renovationPlannerImagesHg: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-stack-hg-de.png',
  ),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-stack-hg-en.png',
  ),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-stack-hg-fr.png',
  ),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-stack-hg-it.png',
  ),
};

const renovationPlannerImagesIs24: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-stack-is24-de.png',
  ),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-stack-is24-en.png',
  ),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-stack-is24-fr.png',
  ),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-stack-is24-it.png',
  ),
};

const phoneMockupImage = computed(() => {
  const imagesByLocale = isHg ? renovationPlannerImagesHg : renovationPlannerImagesIs24;
  const currentLocale = locale.value as HgSupportedLanguage;

  return imagesByLocale[currentLocale] ?? imagesByLocale[HgSupportedLanguage.EN];
});

const goToOverview = () => {
  trackClickEventWithTreatment(
    TRACKING_ACTION.GO_TO_MY_REAL_ESTATE,
    TRACKING_LABEL.RENOVATION_PLANNER_TEASER,
  );

  router.push(OVERVIEW_PAGE_PATH_SOURCE_LANDING[locale.value]);
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.card {
  display: flex;
  flex-direction: column;
  gap: $space-200;
  background-color: $color-violet-0;
  border-radius: $border-radius-xl;
  padding: $space-300 $space-200;
  overflow: hidden;

  &.is24 {
    background-color: $color-grey-30;

    .badge {
      background-color: #0d645c;
      border-color: #0d645c;
      color: #ffffff;
    }
  }

  @include grid-media-query(sm) {
    flex-direction: row;
    align-items: stretch;
    padding: 0;

    .content {
      padding: $space-300;
    }
  }
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: $space-100;
}

.badge {
  width: fit-content;
}

.title {
  @include heading-1;
  margin: 0;
  color: $color-black;
}

.description {
  @include body-2;
  margin: 0;
  color: $color-black;
}

.cta {
  width: fit-content;
  margin-top: $space-100;
  color: $color-blue-60;
}

.imageWrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: $space-200;

  @include grid-media-query(sm) {
    padding: $space-300;
  }
}

.imageWrapperHg {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center right;
}

.visualContainer {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 320px;
}

.backgroundGraphic {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 160%;
  height: auto;
  transform: translate(-50%, -50%);
  z-index: 0;
  pointer-events: none;
}

.image {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 320px;
  height: auto;
}
</style>
